<template>
    <div
        class="-mt-16 md:mt-auto relative md:absolute left-auto lg:left-1/2 md:right-0 lg:right-auto md:-top-4 w-full h-full md:w-[28rem] md:h-[28rem]"
    >
        <div
            class="absolute w-full h-full mt-4 md:mt-8 md:px-28 flex flex-col justify-center text-center text-gray-700 cursor-default"
        >
            <div v-if="step === 1" class="z-0 mx-auto w-52 md:w-full">
                <h3 class="text-xl font-semibold mb-2">Anxious Thoughts</h3>

                <p>
                    Just one
                    <span
                        class="bg-highlight font-semibold px-1 whitespace-nowrap"
                        >anxious thought</span
                    >
                    can spread throughout your mind and change the way you
                    think. Situations that you could manage in the past now may
                    begin to make you worry.
                </p>
            </div>
            <div v-if="step === 2" class="z-0 mx-auto w-52 md:w-full">
                <h3 class="mt-4 md:mt-auto text-xl font-semibold mb-2">
                    Nervous Feelings
                </h3>

                <p>
                    Anxious thoughts give rise to
                    <span
                        class="bg-highlight font-semibold px-1 whitespace-nowrap"
                        >nervous feelings</span
                    >
                    in your body. When stressed, your stomach is tied in knots,
                    your heart beats faster, your breathing quickens, and you
                    begin to sweat.
                </p>
            </div>
            <div v-if="step === 3" class="z-0 mx-auto w-52 md:w-full">
                <h3 class="text-xl font-semibold mb-2">Altered Behavior</h3>

                <p>
                    As your nervous feelings intensify, you may begin to
                    <span
                        class="bg-highlight font-semibold px-1 whitespace-nowrap"
                        >alter your behavior</span
                    >. You prefer to stay within your comfort zone and avoid
                    activities that make you feel self-conscious.
                </p>
            </div>
            <div v-if="step === 4" class="z-0 mx-auto w-52 md:w-full">
                <h3 class="text-xl font-semibold mb-2">Increased Negativity</h3>

                <p>
                    As you feel less like your normal self, your outlook on life
                    grows
                    <span
                        class="bg-highlight font-semibold px-1 whitespace-nowrap"
                        >increasingly negative</span
                    >. This gives rise to new anxious thoughts, and the cycle
                    repeats.
                </p>
            </div>
        </div>

        <div class="z-10 flex items-center justify-center md:gap-x-8">
            <svg
                @mouseover="setStep(1)"
                @mouseout="startCycle()"
                :class="[
                    step === 1
                        ? 'text-highlight transform md:translate-x-1 md:translate-y-1 scale-110 transition duration-500 ease-in-out'
                        : 'text-gray-700',
                    'z-10 shrink-0 ml-8 w-48 h-48 cursor-pointer',
                ]"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 94 80.8"
                fill="currentColor"
            >
                <path
                    d="M90.8,26.16,75.87,17.54,60.94,8.92A2.4,2.4,0,0,0,57.34,11V22.55A61.45,61.45,0,0,0,2,83.62,1.78,1.78,0,0,0,3.78,85.4h8.43A1.78,1.78,0,0,0,14,83.62a49.44,49.44,0,0,1,43.35-49V45.48a2.4,2.4,0,0,0,3.6,2.08l14.93-8.62L90.8,30.32A2.4,2.4,0,0,0,90.8,26.16Z"
                    transform="translate(0 -6.6)"
                    :stroke="step === 1 ? '#374151' : ''"
                    :stroke-miterlimit="step === 1 ? 10 : ''"
                    :stroke-width="step === 1 ? '4px' : ''"
                />
            </svg>
            <svg
                @mouseover="setStep(2)"
                @mouseout="startCycle()"
                :class="[
                    step === 2
                        ? 'text-highlight transform -translate-x-0.5 md:-translate-x-1 md:translate-y-1 scale-110 transition duration-500 ease-in-out'
                        : 'text-gray-700',
                    'z-10 shrink-0 mt-16 w-48 h-48 cursor-pointer',
                ]"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 80.8 94"
                fill="currentColor"
            >
                <path
                    d="M67.84,90.8l8.62-14.93,8.62-14.93A2.4,2.4,0,0,0,83,57.34H71.45A61.45,61.45,0,0,0,10.38,2,1.78,1.78,0,0,0,8.6,3.78v8.43A1.78,1.78,0,0,0,10.38,14a49.44,49.44,0,0,1,49,43.35H48.52a2.4,2.4,0,0,0-2.08,3.6l8.62,14.93L63.68,90.8A2.4,2.4,0,0,0,67.84,90.8Z"
                    transform="translate(-6.6 0)"
                    :stroke="step === 2 ? '#374151' : ''"
                    :stroke-miterlimit="step === 2 ? 10 : ''"
                    :stroke-width="step === 2 ? '4px' : ''"
                />
            </svg>
        </div>

        <div
            class="-mt-16 md:-mt-8 flex items-center justify-center md:gap-x-8"
        >
            <svg
                @mouseover="setStep(4)"
                @mouseout="startCycle()"
                :class="[
                    step === 4
                        ? 'text-highlight transform md:translate-x-1 md:-translate-y-1 scale-110 transition duration-500 ease-in-out'
                        : 'text-gray-700',
                    'z-10 shrink-0 w-48 h-48 cursor-pointer',
                ]"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 80.8 94"
                fill="currentColor"
            >
                <path
                    d="M26.16,3.2,17.54,18.13,8.92,33.06A2.4,2.4,0,0,0,11,36.66H22.55A61.45,61.45,0,0,0,83.62,92a1.78,1.78,0,0,0,1.78-1.78V81.79A1.78,1.78,0,0,0,83.62,80a49.44,49.44,0,0,1-49-43.35H45.48a2.4,2.4,0,0,0,2.08-3.6L38.94,18.13,30.32,3.2A2.4,2.4,0,0,0,26.16,3.2Z"
                    transform="translate(-6.6 0)"
                    :stroke="step === 4 ? '#374151' : ''"
                    :stroke-miterlimit="step === 4 ? 10 : ''"
                    :stroke-width="step === 4 ? '4px' : ''"
                />
            </svg>
            <svg
                @mouseover="setStep(3)"
                @mouseout="startCycle()"
                :class="[
                    step === 3
                        ? 'text-highlight transform -translate-x-0.5 md:-translate-x-1 md:-translate-y-1 scale-110 transition duration-500 ease-in-out'
                        : 'text-gray-700',
                    'z-10 shrink-0 mr-8 mt-16 w-48 h-48 cursor-pointer',
                ]"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 94 80.8"
                fill="currentColor"
            >
                <path
                    d="M3.2,67.84l14.93,8.62,14.93,8.62A2.4,2.4,0,0,0,36.66,83V71.45A61.45,61.45,0,0,0,92,10.38,1.78,1.78,0,0,0,90.22,8.6H81.79A1.78,1.78,0,0,0,80,10.38a49.44,49.44,0,0,1-43.35,49V48.52a2.4,2.4,0,0,0-3.6-2.08L18.13,55.06,3.2,63.68A2.4,2.4,0,0,0,3.2,67.84Z"
                    transform="translate(0 -6.6)"
                    :stroke="step === 3 ? '#374151' : ''"
                    :stroke-miterlimit="step === 3 ? 10 : ''"
                    :stroke-width="step === 3 ? '4px' : ''"
                />
            </svg>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref } from "vue";

const step = ref(1);
let timer: ReturnType<typeof setInterval>;

startCycle();

function setStep(id: number) {
    step.value = id;
    clearInterval(timer);
}

function startCycle() {
    timer = setInterval(
        () => (step.value = step.value + 1 > 4 ? 1 : step.value + 1),
        5000,
    );
}
</script>
